import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ShieldCheck, Wrench, Headphones } from "lucide-react";

export default function Hero({ onBook, onExplore, cms = {} }) {
  const [mounted, setMounted] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    setMounted(true);
    setReducedMotion(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false);
  }, []);

  if (cms.visible === false) return null;

  const eyebrow = cms.eyebrow || "Introducing GenieX";
  const headingLine1 = cms.heading_line1 || "Intelligence";
  const headingAccent = cms.heading_accent || "At Your Command.";
  const description =
    cms.description ||
    "A home that responds before you ask. Ultra-premium smart living, quietly engineered for India's most discerning homes.";
  const primaryLabel = cms.primary_cta_label || "Book a Consultation";
  const primaryLink = cms.primary_cta_link || "";
  const secondaryLabel = cms.secondary_cta_label || "Explore the Experience";
  const secondaryLink = cms.secondary_cta_link || "";
  const desktopImage = cms.desktop_image || "/media/elite-series-teaser-poster.jpg";
  const mobileImage = cms.mobile_image || "";
  const useVideo = !cms.desktop_image && !reducedMotion && !videoFailed;

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[var(--gx-black)] grain"
    >
      {/* Background — Elite Series teaser video, falls back to poster/CMS image */}
      <div className="absolute inset-0">
        {useVideo ? (
          <video
            data-testid="hero-teaser-video"
            className="w-full h-full object-cover opacity-[0.55]"
            src="/media/elite-series-teaser.mp4"
            poster="/media/elite-series-teaser-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <picture>
            {mobileImage && <source media="(max-width: 767px)" srcSet={mobileImage} />}
            <img
              data-testid="hero-fallback-image"
              src={desktopImage}
              alt="GenieX Elite Series"
              onError={(e) => {
                e.currentTarget.src =
                  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85";
              }}
              className="w-full h-full object-cover opacity-[0.55]"
            />
          </picture>
        )}
        <div className="absolute inset-0 vignette" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[var(--gx-bg)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 pt-32 lg:pt-44 pb-16 lg:pb-20 lg:min-h-[100svh] lg:flex lg:flex-col lg:justify-between">
        <div className={`${mounted ? "animate-rise" : "opacity-0"}`}>
          <p className="eyebrow mb-5">{eyebrow}</p>
          <h1 className="h1-display text-white max-w-[14ch]">
            {headingLine1}<br />
            <span className="text-gold">{headingAccent}</span>
          </h1>
          <p className="lead mt-6 lg:mt-8 max-w-[44ch]">
            {description}
          </p>
          <div className="flex flex-wrap items-center gap-3 lg:gap-4 mt-9 lg:mt-12">
            {primaryLink ? (
              <Link to={primaryLink} data-testid="hero-book-consultation" className="btn-gold">
                {primaryLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button onClick={onBook} data-testid="hero-book-consultation" className="btn-gold">
                {primaryLabel} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {secondaryLink ? (
              <Link to={secondaryLink} data-testid="hero-explore" className="btn-outline">
                {secondaryLabel}
              </Link>
            ) : (
              <button onClick={onExplore} data-testid="hero-explore" className="btn-outline">
                {secondaryLabel}
              </button>
            )}
          </div>
        </div>

        {/* Premium Assurance band — sits in the breathing room between hero
            and the Flagship section. Natural flow on mobile (clear top gap),
            anchored to viewport bottom on desktop (justify-between). */}
        <div
          data-testid="hero-assurance-panel"
          className={`mt-20 sm:mt-24 lg:mt-12 ${mounted ? "animate-fade" : "opacity-0"}`}
        >
          <div className="hidden sm:flex items-center gap-2 text-[11px] text-mute tracking-[0.28em] uppercase mb-6">
            <span>Scroll</span>
            <ChevronDown className="w-3.5 h-3.5 text-gold animate-bounce" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-line/60 rounded-2xl overflow-hidden border border-line/80 backdrop-blur-xl">
            <Assurance
              Icon={ShieldCheck}
              title="5-Year Free Replacement"
              sub="A no-questions warranty across the Dynamic Series."
              testId="assurance-warranty"
            />
            <Assurance
              Icon={Wrench}
              title="Professional Installation"
              sub="Certified technicians. Zero damage. White-glove finish."
              testId="assurance-installation"
            />
            <Assurance
              Icon={Headphones}
              title="Remote Support"
              sub="A concierge a tap away — for life, not for a year."
              testId="assurance-support"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Assurance({ Icon, title, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[rgba(10,10,12,0.72)] px-5 py-5 lg:px-7 lg:py-6 flex items-start gap-4 group"
    >
      <span className="w-10 h-10 rounded-full border border-gold/40 bg-gradient-to-br from-[#E2C07A]/15 to-[#8E7338]/10 flex items-center justify-center shrink-0 transition-colors group-hover:border-gold">
        <Icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
      </span>
      <div className="flex flex-col">
        <p className="font-display text-[15px] lg:text-base text-white leading-tight">{title}</p>
        <p className="text-[12px] text-dim mt-1.5 leading-snug max-w-[28ch]">{sub}</p>
      </div>
    </div>
  );
}
